import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { feldHintergrund, titelGröße } from '../lib/format';
import type { Board, Score, Team } from '../lib/types';
import { Konfetti, type Ausbruch } from './Konfetti';

/**
 * Das Bingo-Board.
 *
 * Dieselbe Darstellung für Spieler und Overlay – nur dass im Overlay nicht geklickt werden kann.
 * Zwei Dinge sind hier bewusst gelöst:
 *
 * 1. **Ein fertiges Bingo** wird gefeiert und nicht markiert: einmal kurz Konfetti, danach steht
 *    das Ergebnis einfach in der Teamfarbe in den Feldern. Ein Strich quer über die Reihe würde
 *    dauerhaft über den Aufgaben liegen und sie schlechter lesbar machen.
 * 2. **Das Aufleuchten beim Belegen** merkt sich, welche Felder vorher belegt waren. So blitzt nur
 *    das wirklich neue Feld auf und nicht bei jedem eintreffenden Stand das ganze Board.
 */
export function BingoBoard({
  board,
  teams,
  scores,
  onToggle,
  istAnklickbar = false,
}: {
  board: Board;
  teams: Team[];
  scores: Score[];
  onToggle?: (cellIndex: number) => void;
  istAnklickbar?: boolean;
}) {
  const farbe = (teamId: string) => teams.find((t) => t.id === teamId)?.color ?? '#888888';
  const frischBelegt = useFrischBelegt(board);
  const { ref, zelle } = useZellgröße(board.size);

  return (
    <div className="board-wrapper" ref={ref} style={{ '--zelle': `${zelle}px` } as React.CSSProperties}>
      <div
        className="board"
        style={{ gridTemplateColumns: `repeat(${board.size}, 1fr)` }}
        role={istAnklickbar ? 'grid' : 'presentation'}
      >
        {board.cells.map((cell) => {
          const farben = cell.teamIds.map(farbe);
          const belegt = farben.length > 0;
          const klassen = [
            'cell',
            belegt ? 'claimed' : '',
            frischBelegt.has(cell.index) ? 'flash' : '',
            istAnklickbar ? 'clickable' : '',
          ]
            .filter(Boolean)
            .join(' ');

          const inhalt = (
            <>
              <span className="cell-number">{cell.index + 1}</span>
              <span className="cell-title" style={{ fontSize: titelGröße(cell.title) }}>
                {cell.title}
              </span>
            </>
          );

          return istAnklickbar ? (
            <button
              key={cell.index}
              type="button"
              className={klassen}
              style={{ background: feldHintergrund(farben) }}
              title={cell.description || cell.title}
              onClick={() => onToggle?.(cell.index)}
            >
              {inhalt}
            </button>
          ) : (
            <div
              key={cell.index}
              className={klassen}
              style={{ background: feldHintergrund(farben) }}
            >
              {inhalt}
            </div>
          );
        })}
      </div>

      <Konfetti ausbrüche={ausbrüche(board, scores, teams)} />
    </div>
  );
}

/**
 * Wo für welches Team Konfetti aufgehen soll.
 *
 * Je fertigem Bingo ein Punkt, und zwar in der Mitte der Reihe – dort, wo gerade etwas geschafft
 * wurde. Gerechnet wird in Anteilen der Boardfläche, damit es unabhängig von der Größe sitzt.
 */
function ausbrüche(board: Board, scores: Score[], teams: Team[]): Ausbruch[] {
  const mitte = (feld: number) => ({
    x: ((feld % board.size) + 0.5) / board.size,
    y: (Math.floor(feld / board.size) + 0.5) / board.size,
  });

  return scores.flatMap((score) => {
    const farbe = teams.find((t) => t.id === score.teamId)?.color ?? '#ffffff';
    return score.completedLines.map((linie) => {
      const erstes = mitte(linie.cells[0]!);
      const letztes = mitte(linie.cells[linie.cells.length - 1]!);
      return {
        schlüssel: `${score.teamId}-${linie.kind}-${linie.index}`,
        farbe,
        x: (erstes.x + letztes.x) / 2,
        y: (erstes.y + letztes.y) / 2,
      };
    });
  });
}

/**
 * Misst, wie breit ein Feld gerade ist.
 *
 * Damit richtet sich die Schrift nach der tatsächlichen Größe des Boards – im Stream genauso wie
 * am Handy. Eine Rechnung in Bildschirmeinheiten würde daneben liegen, sobald das Board in einem
 * Kasten sitzt, der nicht die ganze Breite hat.
 */
function useZellgröße(size: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [zelle, setZelle] = useState(96);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const messen = () => {
      const breite = element.getBoundingClientRect().width;
      if (breite > 0) setZelle(breite / size);
    };
    messen();
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(element);
    return () => beobachter.disconnect();
  }, [size]);

  return { ref, zelle };
}

/** Merkt sich den vorigen Stand und meldet die Felder, die gerade neu belegt wurden. */
function useFrischBelegt(board: Board): Set<number> {
  const [frisch, setFrisch] = useState<Set<number>>(new Set());
  const vorher = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    const jetzt = new Map(board.cells.map((c) => [c.index, c.teamIds.length]));
    const neu = new Set<number>();
    for (const [index, anzahl] of jetzt) {
      if (anzahl > (vorher.current.get(index) ?? 0)) neu.add(index);
    }
    vorher.current = jetzt;

    if (neu.size === 0) return;
    setFrisch(neu);
    const uhr = setTimeout(() => setFrisch(new Set()), 650);
    return () => clearTimeout(uhr);
  }, [board]);

  return frisch;
}
